// Truthful feedback for review actions (BOT-449).
//
// Review pages remove rows optimistically. On a failure they used to put the
// rows back and say "failed — the rows were restored", but a request nginx
// timed out (504) keeps running on the server and usually commits, so the rows
// came back as pending while the approval landed moments later. Pages now
// reload the list from the server instead, and these helpers word the message
// by what actually happened.

/** A review request the API answered with a non-2xx status. */
export class ReviewRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ReviewRequestError';
    this.status = status;
  }
}

/** Build the error for a failed review response, preferring the server's `detail`. */
export async function reviewRequestError(res: Response, what: string): Promise<ReviewRequestError> {
  let detail = '';
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (typeof body?.detail === 'string') detail = body.detail;
  } catch {
    // nginx error pages are HTML — fall back to the status.
  }
  return new ReviewRequestError(res.status, detail || `${what} failed: ${res.status}`);
}

/** The banner text for a review action that threw. The caller reloads the list. */
export function describeReviewFailure(label: string, e: unknown): string {
  if (e instanceof ReviewRequestError) {
    // 409 = rows locked by a background job; the API rolled back, nothing changed.
    if (e.status === 409) return `${label} did not go through. ${e.message}`;
    if (e.status === 502 || e.status === 504) {
      return `${label} timed out. It may still complete on the server — the list was reloaded; `
        + 'approving anything that is still shown is safe.';
    }
  }
  const msg = e instanceof Error ? e.message : 'request failed';
  return `${label} failed — the list was reloaded from the server. ${msg}`;
}

/** "X of Y" when a bulk action moved fewer rows than were selected, else null. */
export function describePartialReview(label: string, requested: number, updated: number): string | null {
  if (updated >= requested) return null;
  return `${label}: ${updated} of ${requested} rows updated — the rest were already decided `
    + 'or changed in the meantime. The list was reloaded.';
}
